import { useEffect, useState } from "react";
import { api } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { ShoppingCart, Loader2, RefreshCw, FlaskConical } from "lucide-react";

/** Admin (read-only): abandoned-checkout recovery — who opened Stripe checkout,
 *  what ac_* emails went out, and whether they converted. Times in Brisbane. */
const AdminAbandonedCheckoutsPage = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [scan, setScan] = useState(null);
  const [scanning, setScanning] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const d = await api.get("/admin/marketing/abandoned-checkouts");
      setRows(d.rows || []);
    } catch { toast.error("Failed to load abandoned checkouts"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const runDryScan = async () => {
    setScanning(true);
    try {
      const d = await api.post("/admin/marketing/abandoned-checkouts/scan?dry_run=true", {});
      setScan(d);
      toast.success(`Dry run: ${d.eligible} would be enrolled (nothing sent)`);
    } catch { toast.error("Dry-run scan failed"); }
    finally { setScanning(false); }
  };

  const bris = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString("en-AU", {
        timeZone: "Australia/Brisbane", day: "2-digit", month: "short",
        hour: "2-digit", minute: "2-digit",
      });
    } catch { return iso; }
  };

  const enrolBadge = (s) => {
    if (!s) return <span className="text-muted-foreground">—</span>;
    if (s === "active") return <Badge className="bg-amber-500/15 text-amber-700 border-amber-300">active</Badge>;
    if (s.startsWith("exited:converted")) return <Badge className="bg-green-500/15 text-green-700 border-green-300">converted</Badge>;
    if (s === "completed") return <Badge variant="secondary">finished</Badge>;
    if (s === "paused") return <Badge variant="outline">paused</Badge>;
    return <Badge variant="outline">{s}</Badge>;
  };

  return (
    <div className="space-y-6" data-testid="admin-abandoned-checkouts-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <ShoppingCart className="h-6 w-6" /> Abandoned checkouts
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Read-only. Checkout sessions, recovery emails sent, and conversions. Times shown in Brisbane.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={load} disabled={loading} data-testid="ac-refresh-btn">
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
          <Button onClick={runDryScan} disabled={scanning} data-testid="ac-dry-scan-btn">
            {scanning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FlaskConical className="h-4 w-4 mr-2" />}
            Run scan (dry run)
          </Button>
        </div>
      </div>

      {scan && (
        <div className="rounded-lg border bg-muted/30 p-4 text-sm" data-testid="ac-scan-result">
          <p className="font-medium mb-1">
            Dry run (lookback {scan.lookback_days}d): scanned {scan.sessions_scanned} sessions ·{" "}
            <span className="text-amber-700">{scan.eligible} would be enrolled</span> · nothing was sent.
          </p>
          <p className="text-muted-foreground text-xs">
            skipped: {Object.entries(scan.skipped || {}).map(([k, v]) => `${k}=${v}`).join("  ·  ")}
          </p>
          {(scan.candidates || []).length > 0 && (
            <ul className="mt-2 space-y-1">
              {scan.candidates.map((c, i) => (
                <li key={i} className="font-mono text-xs">
                  {c.email} — {c.plan} — opened {bris(c.session_created_at)} — {c.subscription_status || "free"}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="rounded-lg border overflow-x-auto">
        <table className="w-full text-sm" data-testid="ac-table">
          <thead className="bg-muted/50 text-left">
            <tr>
              <th className="p-3 font-medium">User</th>
              <th className="p-3 font-medium">Plan</th>
              <th className="p-3 font-medium">Opened (Brisbane)</th>
              <th className="p-3 font-medium">Session</th>
              <th className="p-3 font-medium">Recovery</th>
              <th className="p-3 font-medium">Emails sent</th>
              <th className="p-3 font-medium">Converted</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">
                <Loader2 className="h-5 w-5 animate-spin inline" /> Loading…
              </td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={7} className="p-6 text-center text-muted-foreground">No checkout sessions yet.</td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.session_id} className="border-t hover:bg-muted/20" data-testid={`ac-row-${r.session_id}`}>
                <td className="p-3">
                  <div className="font-medium">{r.name || "—"}</div>
                  <div className="text-xs text-muted-foreground">{r.email}</div>
                </td>
                <td className="p-3">{r.plan}</td>
                <td className="p-3 whitespace-nowrap">{bris(r.created_at)}</td>
                <td className="p-3">
                  <Badge variant={r.session_status === "complete" ? "default" : "outline"}>{r.session_status}</Badge>
                </td>
                <td className="p-3">{enrolBadge(r.enrolment_status)}</td>
                <td className="p-3">
                  {r.emails_sent?.length
                    ? r.emails_sent.map((e, i) => (
                        <div key={i} className="text-xs">ac_{e.step} · {bris(e.at)}</div>
                      ))
                    : <span className="text-muted-foreground">—</span>}
                </td>
                <td className="p-3">
                  {r.converted
                    ? <span className="text-green-700 text-xs">Yes · {bris(r.purchase_time)}</span>
                    : <span className="text-muted-foreground">No</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminAbandonedCheckoutsPage;
